import React, { useState, useMemo } from 'react';
import { usePortalContext } from '@/layouts/CustomerPortalLayout';
import { useMarketData } from '@/hooks/useMarketData';
import { formatCurrency, formatPriceCt, formatDate, formatConsumption } from '@/utils/formatters';
import { getAbgaben } from '@/data/abgabenUmlagen';
import {
  Star, Check, Calendar, Clock, TrendingDown, Info, AlertCircle,
  FileText, ChevronDown, ChevronUp, Shield, Zap, X, LayoutGrid, Table as TableIcon
} from 'lucide-react';

const PortalOffers = () => {
  const { data, acceptOffer } = usePortalContext();
  const { marketData, error: marketError } = useMarketData();
  // Ohne belastbare Boersendaten machen wir dem Kunden KEINE Marktaussage.
  const marketOk = !marketError && typeof marketData?.basePrice === 'number';
  const { offers, company } = data;
  const offer = offers[0]; // primary offer

  const [view, setView] = useState('cards'); // cards, table
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [consumption, setConsumption] = useState(company?.annual_consumption_kwh || 100000);
  const [acceptModal, setAcceptModal] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const baseConsumption = company?.annual_consumption_kwh || 100000;

  // Adjust calculated costs based on slider
  const adjustedOptions = useMemo(() => {
    if (!offer?.options) return [];
    const ratio = consumption / baseConsumption;
    return offer.options.map(opt => ({
      ...opt,
      adjusted_annual_cost: (opt.annual_cost_eur || 0) * ratio,
      adjusted_monthly_cost: (opt.monthly_cost_eur || 0) * ratio,
      adjusted_savings: (opt.savings_eur || 0) * ratio,
    }));
  }, [offer, consumption, baseConsumption]);

  if (!offer || offer.options?.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Angebote</h1>
          <p className="text-sm text-muted-foreground mt-1">Ihre persoenlichen Angebote auf einen Blick</p>
        </div>
        <div className="hk-card">
          <div className="hk-card-body text-center py-16">
            <FileText className="mx-auto text-muted-foreground mb-3" size={48} />
            <h3 className="text-lg font-bold text-foreground">Angebote in Vorbereitung</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Unser Team schreibt gerade bei unseren Versorgern aus. Sobald die ersten verbindlichen Preise eingehen, werden sie hier angezeigt.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const daysLeft = offer.valid_until ? Math.max(0, Math.ceil((new Date(offer.valid_until) - new Date()) / 86400000)) : null;
  const recommendedOption = offer.options.find(o => o.is_recommended) || offer.options[0];
  const epexNow = marketData?.basePrice ? formatPriceCt(marketData.basePrice * 100) : '–';

  /**
   * Preisbestandteile aus ECHTEN Daten statt hartkodierter Zahlen.
   *
   * Hier standen frueher feste Werte ("~9 ct/kWh" Netzentgelte, KWKG 0,446 …)
   * und ein erfundener Rueckfallwert von 10 ct fuer den Arbeitspreis. Die
   * Umlagen aendern sich jaehrlich; die Netzentgelte sind regional voellig
   * verschieden. Fehlt ein Wert, steht jetzt "–" statt einer Zahl.
   */
  const abgabenEinzeln = getAbgaben(offer.plz || company?.plz, 'NS').einzeln;
  const ct = (v) => (typeof v === 'number' && Number.isFinite(v) ? `${formatPriceCt(v)} ct/kWh` : '–');
  const preisBestandteile = [
    {
      label: 'Unser Energiepreis (verhandelt)',
      value: ct(recommendedOption?.customer_price_ct_kwh),
      variable: true,
    },
    { label: 'Netzentgelte (regional)', value: ct(offer.netzentgelt_ct_kwh), variable: false },
    { label: 'Stromsteuer', value: ct(abgabenEinzeln.stromsteuer), variable: false },
    { label: 'KWKG-Umlage', value: ct(abgabenEinzeln.kwkg_umlage), variable: false },
    { label: 'Offshore-Netzumlage', value: ct(abgabenEinzeln.offshore_umlage), variable: false },
    { label: 'StromNEV §19-Umlage', value: ct(abgabenEinzeln.stromnev_umlage), variable: false },
    { label: 'Konzessionsabgabe', value: ct(abgabenEinzeln.konzessionsabgabe), variable: false },
  ];

  const handleAccept = async (option) => {
    const result = await acceptOffer(offer.id, option.id);
    if (result.ok) {
      setSubmitted(true);
      setTimeout(() => setAcceptModal(null), 3000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Ihre Angebote</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Angebotsnummer <strong className="font-mono text-primary">{offer.offer_number}</strong>
            {daysLeft !== null && (
              <> &middot; Noch <strong className={daysLeft <= 3 ? 'text-destructive' : 'text-foreground'}>{daysLeft} Tage</strong> gueltig</>
            )}
          </p>
        </div>
        {/* View switcher */}
        <div className="flex items-center bg-card border border-border rounded-lg p-1">
          <button
            onClick={() => setView('cards')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'cards' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
          >
            <LayoutGrid size={14} />Karten
          </button>
          <button
            onClick={() => setView('table')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'table' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
          >
            <TableIcon size={14} />Tabelle
          </button>
        </div>
      </div>

      {/* Personal message */}
      {offer.personal_message && (
        <div className="hk-card" style={{ background: 'linear-gradient(135deg, #e6f2f3 0%, #ffffff 100%)', borderColor: '#007D88' }}>
          <div className="hk-card-body flex items-start gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-primary-foreground font-bold text-xs">{data.sales.full_name.split(' ').map(n => n[0]).join('')}</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nachricht von {data.sales.full_name}</p>
              <p className="text-sm text-foreground mt-1 leading-relaxed">{offer.personal_message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Market context */}
      <div className="hk-card">
        <div className="hk-card-body flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
              <TrendingDown className="text-primary" size={22} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Aktueller Marktkontext</p>
              {marketOk ? (
                <p className="text-sm text-foreground mt-0.5">
                  EPEX Spot <strong>{epexNow} ct/kWh</strong> &middot; Unsere Angebote liegen {marketData?.trend === 'down' ? 'in einem guenstigen Marktumfeld' : 'im aktuellen Marktrahmen'}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground mt-0.5">
                  Der aktuelle Boersenpreis ist gerade nicht abrufbar. Ihr Angebotspreis bleibt davon unberuehrt.
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Gueltig bis</div>
            <div className="text-sm font-bold text-foreground">{offer.valid_until ? formatDate(offer.valid_until) : '–'}</div>
          </div>
        </div>
      </div>

      {/* Interactive calculator */}
      <div className="hk-card">
        <div className="hk-card-header">
          <h6>Interaktiver Kostenrechner</h6>
          <span className="text-xs text-muted-foreground">Was-waere-wenn-Szenarien</span>
        </div>
        <div className="hk-card-body">
          <label className="block">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-foreground">Ihr Jahresverbrauch</span>
              <span className="text-lg font-bold text-primary">{formatConsumption(consumption)}</span>
            </div>
            <input
              type="range"
              min={Math.round(baseConsumption * 0.5)}
              max={Math.round(baseConsumption * 1.5)}
              step={1000}
              value={consumption}
              onChange={e => setConsumption(Number(e.target.value))}
              className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>-50%</span>
              <span>Basis: {formatConsumption(baseConsumption)}</span>
              <span>+50%</span>
            </div>
          </label>
        </div>
      </div>

      {/* Offers — Cards View */}
      {view === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {adjustedOptions.map(opt => (
            <div
              key={opt.id}
              className={`hk-card relative flex flex-col ${opt.is_recommended ? 'ring-2 ring-primary' : ''}`}
              style={opt.is_recommended ? { boxShadow: '0 10px 25px rgba(0, 125, 136, 0.15)' } : {}}
            >
              {opt.is_recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                  <Star size={10} fill="currentColor" />UNSERE EMPFEHLUNG
                </div>
              )}
              <div className="hk-card-body flex-1 flex flex-col">
                <h3 className="font-bold text-foreground">{opt.label}</h3>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-1">
                  {opt.price_type === 'fixed' ? 'Festpreis' : opt.price_type === 'indexed' ? 'Indexiert' : 'Dynamisch'} &middot; {opt.contract_duration_months} Monate
                </div>

                <div className="mt-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-foreground">{formatPriceCt(opt.customer_price_ct_kwh)}</span>
                    <span className="text-sm text-muted-foreground">ct/kWh</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mt-0.5">Arbeitspreis netto</div>
                </div>

                <div className="mt-5 space-y-3 flex-1">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-xs text-muted-foreground">Jahreskosten</span>
                    <span className="text-sm font-bold text-foreground">{formatCurrency(opt.adjusted_annual_cost)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-xs text-muted-foreground">Monatlich</span>
                    <span className="text-sm font-bold text-foreground">{formatCurrency(opt.adjusted_monthly_cost)}</span>
                  </div>
                  {opt.adjusted_savings > 0 && (
                    <div className="flex justify-between items-center py-2 bg-[#e6fbf2] -mx-2 px-3 rounded-lg">
                      <span className="text-xs font-semibold text-[#00a864]">Ihre Ersparnis</span>
                      <span className="text-sm font-bold text-[#00a864]">{formatCurrency(opt.adjusted_savings)}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setAcceptModal(opt)}
                  className={`mt-5 w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
                    opt.is_recommended
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20'
                      : 'border border-primary text-primary hover:bg-accent'
                  }`}
                >
                  Dieses Angebot waehlen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Offers — Table View */}
      {view === 'table' && (
        <div className="hk-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr className="text-left">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Option</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Preistyp</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Laufzeit</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Arbeitspreis</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Jahreskosten</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Ersparnis</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {adjustedOptions.map(opt => (
                  <tr key={opt.id} className={opt.is_recommended ? 'bg-accent/30' : ''}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {opt.is_recommended && <Star size={14} className="text-primary" fill="currentColor" />}
                        <span className="font-semibold text-foreground">{opt.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">{opt.price_type === 'fixed' ? 'Festpreis' : opt.price_type === 'indexed' ? 'Indexiert' : 'Dynamisch'}</td>
                    <td className="px-4 py-4 text-sm text-foreground">{opt.contract_duration_months} Monate</td>
                    <td className="px-4 py-4 text-right font-bold text-foreground">{formatPriceCt(opt.customer_price_ct_kwh)} ct</td>
                    <td className="px-4 py-4 text-right text-sm text-foreground">{formatCurrency(opt.adjusted_annual_cost)}</td>
                    <td className="px-4 py-4 text-right text-sm font-bold text-success">{opt.adjusted_savings > 0 ? formatCurrency(opt.adjusted_savings) : '–'}</td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => setAcceptModal(opt)}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        Waehlen →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Price breakdown - trust/transparency */}
      <div className="hk-card">
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="w-full hk-card-header hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Info size={16} className="text-primary" />
            <h6>Was ist im Preis enthalten?</h6>
          </div>
          {showBreakdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showBreakdown && (
          <div className="hk-card-body border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">
              Die angezeigten Preise sind reine <strong className="text-foreground">Arbeitspreise (netto)</strong>.
              Auf diesen Preis kommen gesetzlich festgelegte Komponenten, die <strong className="text-foreground">bei jedem Versorger identisch</strong> sind:
            </p>
            <div className="space-y-2">
              {preisBestandteile.map((row, i) => (
                <div key={i} className={`flex items-center justify-between py-2 px-3 rounded-lg ${row.variable ? 'bg-accent' : 'bg-muted/50'}`}>
                  <div className="flex items-center gap-2">
                    {row.variable ? <Star size={12} className="text-primary" /> : <Shield size={12} className="text-muted-foreground" />}
                    <span className="text-sm text-foreground">{row.label}</span>
                    {row.variable && <span className="text-[10px] font-bold text-primary uppercase">Wir</span>}
                    {!row.variable && <span className="text-[10px] font-bold text-muted-foreground uppercase">Fix</span>}
                  </div>
                  <span className="text-sm font-semibold">{row.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-[#fff8e0] border border-[#FFC400]/30 rounded-lg flex items-start gap-2">
              <Info size={14} className="text-[#b8860b] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Wichtig:</strong> Nur der Arbeitspreis unterscheidet sich zwischen Versorgern.
                Alle anderen Komponenten sind gesetzlich geregelt und fallen unabhaengig vom Anbieter an.
                Daher ist es besonders wichtig, den besten Arbeitspreis zu verhandeln — genau das machen wir.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Accept Modal */}
      {acceptModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !submitted && setAcceptModal(null)}>
          <div className="bg-card rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-[#e6fbf2] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="text-[#00a864]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-foreground">Vielen Dank!</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Wir haben Ihre Entscheidung erhalten und melden uns in Kuerze mit den Vertragsunterlagen.
                </p>
              </div>
            ) : (
              <>
                <div className="p-6 border-b border-border flex items-center justify-between">
                  <h3 className="text-lg font-bold text-foreground">Angebot bestaetigen</h3>
                  <button onClick={() => setAcceptModal(null)} className="p-1 text-muted-foreground hover:text-foreground">
                    <X size={18} />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">Sie waehlen folgendes Angebot:</p>
                  <div className="bg-accent p-4 rounded-lg">
                    <div className="font-bold text-foreground">{acceptModal.label}</div>
                    <div className="text-2xl font-bold text-primary mt-1">{formatPriceCt(acceptModal.customer_price_ct_kwh)} ct/kWh</div>
                    <div className="text-xs text-muted-foreground mt-1">Laufzeit {acceptModal.contract_duration_months} Monate</div>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                    Mit der Auswahl geben Sie eine unverbindliche Absichtserklaerung ab. Wir melden uns mit den vollstaendigen Vertragsunterlagen und einer Vollmacht zur Unterschrift. Erst danach ist der Vertrag rechtsverbindlich.
                  </div>
                </div>
                <div className="p-6 border-t border-border flex gap-3">
                  <button onClick={() => setAcceptModal(null)} className="flex-1 border border-border py-2.5 rounded-lg font-semibold text-sm hover:bg-muted">
                    Abbrechen
                  </button>
                  <button onClick={() => handleAccept(acceptModal)} className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-lg font-semibold text-sm hover:bg-primary/90">
                    Bestaetigen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalOffers;
